import * as React from "react";
import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Card,
	CircularProgress,
	Divider,
	FormControl,
	FormHelperText,
	Grid,
	ListSubheader,
	Menu,
	MenuItem,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ArrowCounterClockwiseIcon } from "@phosphor-icons/react/dist/ssr/ArrowCounterClockwise";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { XIcon } from "@phosphor-icons/react/dist/ssr/X";

import type { CampaignRequestCaptureField, NotificationPreview } from "@/hooks/api/use-campaign-requests";
import {
	useCampaignRequestLeadAnalysis,
	useCampaignRequestNotificationPreview,
} from "@/hooks/api/use-campaign-requests";

import { formatQuantity } from "../constants";
import type { TemplateSegment } from "../notification-template";
import {
	firstVariable,
	humaniseVariable,
	parseTemplate,
	serialiseTemplate,
	VARIABLE_LABELS,
} from "../notification-template";
import { TEMPLATE_VARIABLE_GROUPS } from "./reference";

/**
 * Who hears about a conversion.
 *
 * One template for the whole campaign, differentiated by variable: we own the structure — greeting,
 * outcome, summary, recording link — and the outcome variable carries the difference between a
 * booked demo and a referral. The client writes one note, not one per outcome.
 *
 * Recipients are variables resolved per lead rather than addresses, and every column of an uploaded
 * list is addressable, so `sales_rep` and `sales_manager` are available without anyone configuring
 * a mapping. To takes one address and BCC takes the rest, which is already how the mail service
 * behaves.
 *
 * The client never sees the generated template (UX §08). The stored value is still exactly the
 * `{{variable}}` string the summary processor resolves — `getTplParams` and `tplReplace` on the API
 * depend on those keys, and an existing campaign has to keep working — but on screen a variable is
 * a pill carrying a human label, and the braces only exist on the way in and out of this component.
 *
 * The body is theirs to write. It used to be ours alone, with one field to append a paragraph to
 * it, which is a note about the email rather than the email: a client who wanted their own
 * greeting, their own order, or a line their reps would actually read had nowhere to say so. The
 * same pill field does the whole body, `bodyTemplate` stores what they wrote verbatim, and an
 * untouched request stores nothing — so "back to the standard email" is one write and a campaign
 * that never overrode the body follows the template as we improve it.
 */

// ---------------------------------------------------------------------------------------------
// The email before the client changes a word of it
// ---------------------------------------------------------------------------------------------

/**
 * The body the API composes, line for line — `PLATFORM_BODY_TEMPLATE` in
 * `notification-preview.service.ts`, which is also what provisioning writes into the campaign.
 *
 * Held here as well because this is the one screen that has to show the default *before* it has
 * been saved, and a client editing a body that differs from the one we send would be approving
 * the wrong email. The two are the same string; the preview card is the check on that, because it
 * comes back from the server against a real row.
 */
const PLATFORM_BODY_LINES = [
	"Hi,",
	"",
	"Your AI agent spoke with {{contact__name}} at {{contact__company}} on {{last_call__dateTime}}, for {{last_call__duration}}.",
	"",
	"Outcome: {{lead__disposition}}",
	"Summary: {{lead__summary}}",
	"{{client__note}}",
	"Listen to the call → {{call__url}}",
];

/**
 * The standard email, with a note already saved against the request written into it.
 *
 * `{{client__note}}` is a splice rather than a line of its own: empty, it collapses to the blank
 * line that was already there; present, it takes a blank line on each side. Exactly what the API
 * does with the same token, so what is on screen is what the preview resolves.
 */
export function platformBody(clientNote: string): string {
	const note = clientNote.trim();

	return PLATFORM_BODY_LINES.map((line) =>
		line === "{{client__note}}" ? (note === "" ? "" : `\n${note}\n`) : line
	).join("\n");
}

/**
 * What the preview card is showing, and whether that is a failure.
 *
 * Four states rather than two. Three of them end with the same fallback on screen — the template
 * as the client wrote it, unresolved — and that is exactly why the caption has to say which one
 * happened: "there is no list to resolve against" and "we asked your list and it failed" are
 * different facts, and the second one was being reported as the first.
 */
function previewNote(
	preview: NotificationPreview | null,
	failed: boolean,
	hasLeadList: boolean
): { failed: boolean; text: string } {
	if (preview?.fromRealRow) {
		return {
			failed: false,
			text: `Contact details read off your uploaded list${
				preview.sampleContact ? ` — ${preview.sampleContact}` : ""
			}. The outcome and summary are an example.`,
		};
	}

	if (failed) {
		return {
			failed: true,
			text: "We couldn't resolve this against your list just now, so this is your template as written rather than what a contact would receive.",
		};
	}

	if (!hasLeadList) return { failed: false, text: "Structure only, until a list is uploaded." };

	// A list is uploaded and the API answered, but no row came back to resolve against — an empty
	// file, or every row dropped on the way in. Still not the same sentence as "no list yet".
	if (preview) {
		return { failed: true, text: "No row came back from your uploaded list, so nothing here is a resolved value." };
	}

	return { failed: false, text: "Your template as written, until a row comes back from your list." };
}

interface DisplayVariable {
	/** The key written into the template. Never shown. */
	name: string;
	label: string;
	help: string;
}

interface DisplayGroup {
	title: string;
	variables: DisplayVariable[];
}

// ---------------------------------------------------------------------------------------------
// The token field
// ---------------------------------------------------------------------------------------------

/**
 * A variable, inside a sentence.
 *
 * Tinted rather than chipped. A variable has to be obviously not-typed-by-you, and that is all it
 * has to be: at chip weight — a filled capsule, its own height, a delete button riding in the
 * middle of the line — four of them turn one sentence into a row of buttons and force the line
 * apart. Set at the surrounding text's size and line height, so a line carrying four of them is
 * exactly as tall as a line carrying none, and the comma after one sits against the word.
 */
const VariableToken = styled("span")(({ theme }) => ({
	backgroundColor: "var(--mui-palette-primary-50)",
	borderRadius: "4px",
	color: "var(--mui-palette-primary-700)",
	font: "inherit",
	lineHeight: "inherit",
	// Tight, because what follows a variable is usually a comma or a full stop and it has to look
	// like it belongs to the sentence rather than to the token.
	padding: "0 3px",
	whiteSpace: "nowrap",
	...theme.applyStyles("dark", {
		backgroundColor: "var(--mui-palette-primary-900)",
		color: "var(--mui-palette-primary-200)",
	}),
}));

/**
 * The same variable where it can be taken out again.
 *
 * The delete sits outside the flow — absolute, revealed on hover — because the alternative is a
 * glyph inside every token, which is the weight we just took out of the line and a gap between
 * the word and the punctuation after it. Keyboard removal is backspace, which is what the caret
 * is already there for; this is for the hand on the mouse.
 */
function VariablePill({ label, onRemove }: { label: string; onRemove?: () => void }): React.JSX.Element {
	if (!onRemove) return <VariableToken>{label}</VariableToken>;

	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				position: "relative",
				"&:hover .VariablePill-remove, & .VariablePill-remove:focus-visible": { opacity: 1 },
			}}
		>
			<VariableToken>{label}</VariableToken>
			<Box
				component="button"
				type="button"
				className="VariablePill-remove"
				aria-label={`Remove ${label}`}
				tabIndex={-1}
				// Mousedown would blur the run the caret is in first, which commits the field and
				// asks for a preview of a template we are about to change again.
				onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
				onClick={onRemove}
				sx={{
					alignItems: "center",
					backgroundColor: "var(--mui-palette-background-paper)",
					border: "1px solid var(--mui-palette-divider)",
					borderRadius: "50%",
					color: "var(--mui-palette-text-secondary)",
					cursor: "pointer",
					display: "flex",
					height: 15,
					justifyContent: "center",
					opacity: 0,
					padding: 0,
					position: "absolute",
					right: -6,
					top: -6,
					transition: "opacity 120ms",
					width: 15,
					"&:hover": { color: "var(--mui-palette-text-primary)" },
				}}
			>
				<XIcon size={9} weight="bold" />
			</Box>
		</Box>
	);
}

/**
 * One run of ordinary typing between two pills. Borderless on purpose: the field around it is
 * what looks like an input, and this is a word inside it.
 *
 * Laid over its own measurement rather than sized by one — see `TextRun`. Out of flow, so the only
 * thing deciding how wide a run of words is, is the words.
 */
const TokenInput = styled("input")({
	background: "transparent",
	border: "none",
	color: "var(--mui-palette-text-primary)",
	font: "inherit",
	inset: 0,
	outline: "none",
	padding: 0,
	position: "absolute",
	"&::placeholder": { color: "var(--mui-palette-text-secondary)", opacity: 1 },
});

/**
 * The same run, in a body.
 *
 * A sentence is longer than the field is wide, and an `input` answers that by scrolling the words
 * out of sight — which is unreadable for a paragraph and fine for a subject line. This wraps
 * inside the box its measurement gives it; the newlines are still segments, so what it holds is
 * never more than one line of the stored template.
 */
const TokenTextarea = styled("textarea")({
	background: "transparent",
	border: "none",
	color: "var(--mui-palette-text-primary)",
	font: "inherit",
	inset: 0,
	outline: "none",
	overflow: "hidden",
	padding: 0,
	position: "absolute",
	resize: "none",
	"&::placeholder": { color: "var(--mui-palette-text-secondary)", opacity: 1 },
});

interface TemplateFieldProps {
	label: string;
	/**
	 * The label names the field for a screen reader either way; this is whether it is also printed
	 * above it. Off where the field says what it is by what is in it.
	 */
	hideLabel?: boolean;
	/** The stored template. Edited here, never displayed as itself. */
	value: string;
	onChange: (value: string) => void;
	/**
	 * The value has settled — the field lost focus, or a variable was inserted from the picker.
	 * Everything expensive hangs off this rather than off `onChange`.
	 */
	onCommit: (value: string) => void;
	groups: DisplayGroup[];
	labelFor: (name: string) => string;
	helperText?: React.ReactNode;
	placeholder?: string;
	/** A recipient line: several entries, comma-separated, the way the mail service splits them. */
	list?: boolean;
	/** Prose over several lines — the body. Enter starts a new one; everything else is unchanged. */
	lines?: boolean;
	readOnly?: boolean;
}

/**
 * A field whose variables are pills and whose only way to gain one is the picker beside it.
 *
 * The text between the pills is ordinary typing — a literal address on a recipient line, the words
 * around a variable in a subject — so each run of it is its own small input and the caret moves
 * through the field as though it were one. Backspace at the start of a run removes the pill in
 * front of it, which is the only behaviour anybody tries.
 */
function TemplateField({
	label,
	hideLabel = false,
	value,
	onChange,
	onCommit,
	groups,
	labelFor,
	helperText,
	placeholder,
	list = false,
	lines = false,
	readOnly = false,
}: TemplateFieldProps): React.JSX.Element {
	const fieldId = React.useId();
	const segments = React.useMemo(() => parseTemplate(value, lines), [value, lines]);

	const inputs = React.useRef(new Map<number, HTMLInputElement | HTMLTextAreaElement>());
	/** Where the caret was in which text run, so an inserted variable lands where they were. */
	const caret = React.useRef<{ index: number; offset: number } | null>(null);
	const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
	/** Set after an edit that moves the caret somewhere the browser cannot work out for itself. */
	const [restore, setRestore] = React.useState<{ index: number; offset: number } | null>(null);

	React.useEffect(() => {
		if (!restore) return;

		const element = inputs.current.get(restore.index);
		element?.focus();
		element?.setSelectionRange(restore.offset, restore.offset);
		setRestore(null);
	}, [restore]);

	const write = (next: TemplateSegment[], settled: boolean): void => {
		const template = serialiseTemplate(next);
		onChange(template);
		if (settled) onCommit(template);
	};

	const textAt = (index: number): string => {
		const segment = segments[index];
		return segment && segment.kind === "text" ? segment.text : "";
	};

	/**
	 * Remove a pill or a line ending and join the prose either side of it back into one run.
	 *
	 * The same operation for both because they sit in the same place — between two runs of text —
	 * and backspacing at the start of a line has to do whichever of the two is in front of it.
	 */
	const removeAt = (index: number): void => {
		const before = textAt(index - 1);
		const after = textAt(index + 1);
		const next = [...segments];
		next.splice(index - 1, 3, { kind: "text", text: before + after });

		// A pill is a deliberate removal and settles; a line ending is mid-sentence typing and
		// waits for the blur, so a held backspace does not ask the server for a preview per line.
		write(next, segments[index]?.kind === "variable");
		setRestore({ index: index - 1, offset: before.length });
	};

	const insertVariable = (name: string): void => {
		const target = caret.current ?? { index: segments.length - 1, offset: textAt(segments.length - 1).length };
		const text = textAt(target.index);
		const offset = Math.min(target.offset, text.length);

		let before = text.slice(0, offset);
		const after = text.slice(offset);

		// A recipient line is comma-separated on the API, so a second name needs the comma the
		// client would otherwise have to know to type.
		const preceding = serialiseTemplate(segments.slice(0, target.index)) + before;
		if (list && preceding.trim() !== "" && !/,\s*$/.test(preceding)) before = `${before.trimEnd()}, `;

		const next = [...segments];
		next.splice(
			target.index,
			1,
			{ kind: "text", text: before },
			{ kind: "variable", name },
			{ kind: "text", text: after }
		);

		write(next, true);
		setAnchor(null);
		setRestore({ index: target.index + 2, offset: 0 });
	};

	/** Enter splits the run at the caret and puts a line ending between the halves. */
	const breakLine = (index: number, offset: number): void => {
		const text = textAt(index);
		const next = [...segments];
		next.splice(
			index,
			1,
			{ kind: "text", text: text.slice(0, offset) },
			{ kind: "break" },
			{ kind: "text", text: text.slice(offset) }
		);

		write(next, false);
		setRestore({ index: index + 2, offset: 0 });
	};

	const empty = segments.length === 1 && textAt(0) === "";
	/** The last run on its line, so it takes the space to the right and can be clicked into. */
	const endsLine = (index: number): boolean => index === segments.length - 1 || segments[index + 1]?.kind === "break";
	/** A run that is a whole line by itself. Empty, that is a blank line rather than a gap. */
	const wholeLine = (index: number): boolean =>
		endsLine(index) && (index === 0 || segments[index - 1]?.kind === "break");

	/**
	 * Everything one run of prose does, whichever element is holding it.
	 *
	 * Shared because the two differ in one thing only — whether the words wrap — and every
	 * behaviour under them is the same behaviour: the caret is tracked so an inserted variable
	 * lands where they were, Enter ends the line, and backspace at the start takes whatever is in
	 * front of it.
	 */
	const run = (index: number, text: string) => ({
		"aria-label": index === 0 ? label : `${label}, continued`,
		id: `${fieldId}-${index}`,
		placeholder: empty ? placeholder : undefined,
		ref: (element: HTMLInputElement | HTMLTextAreaElement | null) => {
			if (element) inputs.current.set(index, element);
			else inputs.current.delete(index);
		},
		value: text,
		onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const next = [...segments];
			next[index] = { kind: "text", text: event.target.value };
			caret.current = { index, offset: event.target.selectionStart ?? event.target.value.length };
			write(next, false);
		},
		onSelect: (event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			caret.current = { index, offset: event.currentTarget.selectionStart ?? 0 };
		},
		onBlur: () => onCommit(value),
		onKeyDown: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const element = event.currentTarget;

			if (lines && event.key === "Enter") {
				event.preventDefault();
				breakLine(index, element.selectionStart ?? element.value.length);
				return;
			}

			const atStart = element.selectionStart === 0 && element.selectionEnd === 0;
			if (event.key !== "Backspace" || !atStart || index === 0) return;

			// Whatever is in front of the caret — the pill, or the end of the line above. Either
			// way it is the only thing backspace could mean here.
			event.preventDefault();
			removeAt(index - 1);
		},
	});

	/**
	 * A run of prose, exactly as wide as the words in it.
	 *
	 * The width used to be `text.length` in `ch`, which is the width of a zero — every space too
	 * wide, every `i` too wide, and a line of prose visibly ragged around its pills. So the run
	 * carries a copy of its own text, hidden, laid out by the same font in the same box; that copy
	 * is what has a size, and the input is stretched over it. In a body it wraps, which is also
	 * where the height comes from, so nothing has to be measured in JavaScript and corrected after
	 * paint. The zero-width space keeps an empty run — a blank line, the gap between two pills —
	 * one line tall rather than nothing at all.
	 */
	const textRun = (index: number, text: string): React.JSX.Element => {
		const grow = endsLine(index);

		return (
			<Box
				component="span"
				key={`text-${index}`}
				sx={{
					display: "block",
					flexGrow: grow ? 1 : 0,
					flexShrink: 0,
					maxWidth: "100%",
					// A run at the end of a line takes the rest of it, so there is somewhere to click
					// to put the caret after the last word. In a body it may also be nothing at all:
					// a minimum would wrap an empty run onto a line of its own and open a gap under
					// a full line. Between two pills it stays wide enough to click into.
					minWidth: grow ? (lines ? 0 : 72) : 4,
					position: "relative",
				}}
			>
				<Box
					aria-hidden
					component="span"
					sx={{
						display: "block",
						font: "inherit",
						overflow: "hidden",
						overflowWrap: "break-word",
						visibility: "hidden",
						whiteSpace: lines ? "pre-wrap" : "pre",
					}}
				>
					{`${text}\u200B`}
				</Box>

				{lines ? <TokenTextarea rows={1} {...run(index, text)} /> : <TokenInput type="text" {...run(index, text)} />}
			</Box>
		);
	};

	const picker = readOnly ? null : (
		<Button size="small" startIcon={<PlusIcon />} onClick={(event) => setAnchor(event.currentTarget)}>
			Add a variable
		</Button>
	);

	return (
		<FormControl fullWidth>
			{/* One picker per field, above it, where it reads as something you do to the field —
			    rather than one above and a second one sitting inside the body under the last line,
			    where it looked like the last thing in the email. */}
			{hideLabel && readOnly ? null : (
				<Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", minHeight: 30, mb: "2px" }}>
					{hideLabel ? (
						<span />
					) : (
						<Typography
							component="label"
							htmlFor={`${fieldId}-0`}
							variant="body2"
							color="text.secondary"
							sx={{ fontWeight: 500 }}
						>
							{label}
						</Typography>
					)}
					{picker}
				</Stack>
			)}

			<Box
				sx={{
					// Prose is laid out from the top of each line: every run and every pill is one
					// line box tall, so a line carrying pills sits on the same rhythm as one that
					// does not. A single-line field has one row and centres it in the box.
					alignItems: lines ? "flex-start" : "center",
					borderRadius: "8px",
					border: "1px solid var(--mui-palette-OutlinedInput-border)",
					boxShadow: readOnly ? "none" : "var(--mui-shadows-1)",
					display: "flex",
					flexWrap: "wrap",
					fontSize: "1rem",
					// No gap anywhere: the spaces between the words are in the template, and a gap
					// is what put a hole between a pill and the comma after it.
					gap: 0,
					lineHeight: 1.5,
					minHeight: 40,
					opacity: readOnly ? 0.7 : 1,
					position: "relative",
					px: "12px",
					py: "6px",
					"&::before": {
						borderRadius: "inherit",
						bottom: 0,
						boxShadow: "0 0 0 calc(var(--Field-focused, 0) * 2px) var(--mui-palette-primary-main)",
						content: '" "',
						left: 0,
						pointerEvents: "none",
						position: "absolute",
						right: 0,
						top: 0,
					},
					"&:focus-within": { "&::before": { "--Field-focused": 1 } },
				}}
			>
				{readOnly && empty ? (
					<Typography variant="body2" color="text.secondary">
						— not set
					</Typography>
				) : null}

				{segments.map((segment, index) =>
					segment.kind === "break" ? (
						// Nothing to see and nothing to focus: it ends the row the flex container is
						// filling, which is what makes the next run start a line of its own.
						<Box key={`break-${index}`} sx={{ flexBasis: "100%", height: 0 }} />
					) : segment.kind === "variable" ? (
						<VariablePill
							// Positional by nature: the same variable can appear twice in one subject.
							key={`variable-${index}`}
							label={labelFor(segment.name)}
							onRemove={readOnly ? undefined : () => removeAt(index)}
						/>
					) : readOnly ? (
						segment.text === "" ? (
							// A blank line between two paragraphs is content once the client owns the
							// body. An empty run either side of a pill still contributes nothing.
							lines && wholeLine(index) ? (
								<Box key={`text-${index}`} sx={{ height: "1.5em", width: "100%" }} />
							) : null
						) : (
							// The spaces around a variable are part of the text, and a flex item drops
							// the ones on its ends unless it is told to keep them.
							<Box key={`text-${index}`} component="span" sx={{ whiteSpace: "pre-wrap" }}>
								{segment.text}
							</Box>
						)
					) : (
						textRun(index, segment.text)
					)
				)}
			</Box>

			{helperText ? <FormHelperText>{helperText}</FormHelperText> : null}

			<Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
				{groups.flatMap((group) => [
					<ListSubheader key={`${group.title}-header`}>{group.title}</ListSubheader>,
					...group.variables.map((variable) => (
						// The description sits under the name rather than in a tooltip: it is the
						// thing that tells them which of two similar variables they want.
						<MenuItem key={variable.name} onClick={() => insertVariable(variable.name)} sx={{ display: "block" }}>
							<Typography variant="body2">{variable.label}</Typography>
							<Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: 320 }}>
								{variable.help}
							</Typography>
						</MenuItem>
					)),
				])}
			</Menu>
		</FormControl>
	);
}

/** A stored template read back out as prose with pills — never as braces. */
function TemplateText({
	template,
	labelFor,
	empty,
}: {
	template: string;
	labelFor: (name: string) => string;
	empty: string;
}): React.JSX.Element {
	if (template.trim() === "") return <React.Fragment>{empty}</React.Fragment>;

	return (
		<React.Fragment>
			{parseTemplate(template).map((segment, index) => {
				if (segment.kind === "variable") {
					// No margin of its own: the spaces either side of it are in the template, and
					// the punctuation after it belongs against the word.
					return <VariableToken key={`variable-${index}`}>{labelFor(segment.name)}</VariableToken>;
				}

				// Only reachable from a template parsed into lines; the callers here keep their
				// newlines inside the text and lay them out with `pre-line`.
				if (segment.kind === "break") return <br key={`break-${index}`} />;

				return <React.Fragment key={`text-${index}`}>{segment.text}</React.Fragment>;
			})}
		</React.Fragment>
	);
}

// ---------------------------------------------------------------------------------------------
// The editor
// ---------------------------------------------------------------------------------------------

/**
 * Exactly what the preview is a function of, trimmed.
 *
 * Every field on the draft that the server does *not* resolve is deliberately absent: the
 * exclusion switch changes which contacts get loaded, not what this one email looks like, and
 * including it would spend a decryption of the client's lead list on a toggle.
 */
function previewInputs(draft: NotificationDraft): PreviewInputs {
	return {
		toTemplate: draft.toTemplate.trim(),
		bccTemplate: draft.bccTemplate.trim(),
		subjectTemplate: draft.subjectTemplate.trim(),
		clientNote: draft.clientNote.trim(),
		bodyTemplate: draft.bodyTemplate.trim(),
		fallbackEmail: draft.fallbackEmail.trim(),
	};
}

interface PreviewInputs {
	toTemplate: string;
	bccTemplate: string;
	subjectTemplate: string;
	clientNote: string;
	bodyTemplate: string;
	fallbackEmail: string;
}

export interface NotificationDraft {
	toTemplate: string;
	bccTemplate: string;
	subjectTemplate: string;
	/**
	 * The paragraph the platform body used to append for a client who had no way to write the rest
	 * of it. Kept because requests already carry one and the API still reads it while the body is
	 * ours; the moment the client edits the body it is inside what they wrote, and this is cleared.
	 */
	clientNote: string;
	/** The client's own body. Empty means the standard email — never a stored copy of it. */
	bodyTemplate: string;
	fallbackEmail: string;
	/** "Exclude those 12 contacts" — the second action under the empty-rep warning. */
	excludeContactsWithoutRecipient: boolean;
}

export interface NotificationEditorProps {
	value: NotificationDraft;
	onChange: (value: NotificationDraft) => void;
	/** The request the preview and the empty-column check are asked against. */
	requestId: string;
	/** Every capture field from the section above becomes a variable, so it is offered here too. */
	captureFields: CampaignRequestCaptureField[];
	campaignTitle: string;
	/** True once a lead list has been uploaded — without one there is no row to resolve against. */
	hasLeadList: boolean;
	/**
	 * Whether the contacts with no recipient of their own are currently heading nowhere.
	 *
	 * Reported upwards because this editor lives behind a tab now, and the one warning on the step
	 * that costs the client conversions cannot be a thing they only see if they open the right one.
	 * Pass a stable function — a `setState` setter is one.
	 */
	onRecipientGap?: (unanswered: boolean) => void;
	/** The request is settled: the templates and the preview stay readable, nothing is editable. */
	readOnly?: boolean;
}

export function NotificationEditor({
	value,
	onChange,
	requestId,
	captureFields,
	campaignTitle,
	hasLeadList,
	onRecipientGap,
	readOnly = false,
}: NotificationEditorProps): React.JSX.Element {
	const fallbackRef = React.useRef<HTMLInputElement | null>(null);

	/**
	 * What the client has settled on, as against what they are part-way through typing.
	 *
	 * Both server calls on this screen open the uploaded lead list — they decrypt it and write a
	 * row into that document's access trail — so both hang off this rather than off `value`. It
	 * moves on blur, and when a variable is picked from the menu, and at no other time.
	 */
	const [committed, setCommitted] = React.useState<PreviewInputs>(() => previewInputs(value));

	/**
	 * The empty-column check, against the list they just uploaded.
	 *
	 * UX §08 note 7 calls this the important one: a missing rep means no email is sent and nothing
	 * visibly fails, and catching it here — against the real list, against the variable they
	 * actually chose — is the only cheap moment. Asked against the To template as it currently
	 * reads, unsaved, because changing that template is one of the two fixes.
	 */
	const repVariable = firstVariable(committed.toTemplate);
	const {
		data: analysis,
		isError: analysisFailed,
		refetch: recheckList,
	} = useCampaignRequestLeadAnalysis(hasLeadList ? requestId : null, repVariable);
	const repColumn = analysis?.repColumn ?? null;

	/**
	 * The preview, resolved server-side through the same `{{variable}}` engine that sends the real
	 * email (§08 note 8).
	 *
	 * Asked when a field settles, never as it is typed. Every one of these decrypts the client's
	 * lead list on the server and writes a row into that document's access trail, so a 600ms
	 * debounce across four fields turned composing one sentence into a dozen reads of the client's
	 * contacts — and a dozen entries in the log that exists to answer "who opened this file".
	 *
	 * Two things hold that down. `committed` only moves on blur or on a variable being picked, and
	 * the request is skipped outright when the settled values match the ones already previewed —
	 * so tabbing through the form, or typing a word and deleting it again, costs nothing.
	 */
	const previewMutation = useCampaignRequestNotificationPreview();
	const { mutate: buildPreview } = previewMutation;
	const preview = previewMutation.data ?? null;

	const previewed = React.useRef<string | null>(null);

	React.useEffect(() => {
		if (!hasLeadList) return;

		const asked = JSON.stringify(committed);
		if (previewed.current === asked) return;
		previewed.current = asked;

		buildPreview({
			requestId,
			data: {
				toTemplate: committed.toTemplate || null,
				bccTemplate: committed.bccTemplate || null,
				subjectTemplate: committed.subjectTemplate || null,
				clientNote: committed.clientNote || null,
				// Null asks the API for the standard body, which is the same string this file
				// shows while nothing has been overridden — so the card never contradicts the field.
				bodyTemplate: committed.bodyTemplate || null,
				// Sent so the card can show the fallback catching a row whose rep column is empty,
				// which is the only way the client sees that the field does anything.
				fallbackEmail: committed.fallbackEmail || null,
			},
		});
	}, [buildPreview, committed, hasLeadList, requestId]);

	/**
	 * Ask again for the same templates.
	 *
	 * The effect skips whatever it has already asked for, so forgetting that is half of it; the
	 * other half is a `committed` it will notice, which is the same values in a new object because
	 * nothing about them changed — only our answer for them is missing.
	 */
	const retryPreview = (): void => {
		previewed.current = null;
		setCommitted((current) => ({ ...current }));
	};

	/** Which of the four things the card below is showing, and whether it is a failure. */
	const note = previewNote(preview, previewMutation.isError, hasLeadList);

	/**
	 * The picker, grouped and named for a person: the group titles say where a value comes from,
	 * and every entry carries a label rather than the key it writes.
	 */
	const groups = React.useMemo<DisplayGroup[]>(() => {
		const known: DisplayGroup[] = TEMPLATE_VARIABLE_GROUPS.map((group) => ({
			title: group.title,
			variables: group.variables.map((variable) => ({
				name: variable.name,
				label: VARIABLE_LABELS[variable.name] ?? humaniseVariable(variable.name),
				help: variable.help,
			})),
		}));

		if (captureFields.length === 0) return known;

		return [
			...known,
			{
				title: "Captured on this call",
				variables: captureFields.map((field) => ({
					name: field.appearsAs,
					// The client's own words for their own field: the title they gave it.
					label: field.title,
					help: field.description || field.title,
				})),
			},
		];
	}, [captureFields]);

	const labelFor = React.useMemo(() => {
		const index = new Map<string, string>();
		for (const group of groups) {
			for (const variable of group.variables) index.set(variable.name, variable.label);
		}

		return (name: string): string => index.get(name.trim()) ?? humaniseVariable(name);
	}, [groups]);

	const patch = (changes: Partial<NotificationDraft>): void => onChange({ ...value, ...changes });

	/**
	 * The same write, plus "and this is a finished thought".
	 *
	 * Takes the change rather than reading `value`, because a commit that follows an `onChange` in
	 * the same handler would otherwise settle on the value from before the edit.
	 */
	const commit = (changes: Partial<NotificationDraft>): void => {
		const next = { ...value, ...changes };
		onChange(next);
		setCommitted(previewInputs(next));
	};

	/**
	 * The missing-recipient case is live and they have not chosen to drop those contacts, so the
	 * fallback is the only thing standing between a conversion and silence. Asked for rather than
	 * defaulted: the old default was a reserved test domain, which delivers nowhere and looks
	 * exactly like an answer.
	 */
	const fallbackNeeded = Boolean(repColumn && repColumn.missing > 0) && !value.excludeContactsWithoutRecipient;
	const fallbackMissing = fallbackNeeded && value.fallbackEmail.trim() === "";

	React.useEffect(() => {
		onRecipientGap?.(fallbackMissing);
	}, [fallbackMissing, onRecipientGap]);

	/**
	 * The email on screen: theirs once they have written one, ours until then.
	 *
	 * Editing it writes the whole string, note and all — which is why the note is folded in here
	 * rather than left to be appended afterwards. Clearing the field back to nothing hands the
	 * standard email back, and so does the button under it.
	 */
	const bodyIsTheirs = value.bodyTemplate.trim() !== "";
	const body = bodyIsTheirs ? value.bodyTemplate : platformBody(value.clientNote);

	/**
	 * What a write from the body field changes.
	 *
	 * Nothing, where the text came back as it went in. The note is cleared on a real edit because
	 * it is already inside the text they edited — the standard body the field opens with has it
	 * spliced in, so leaving it set would have the API append it a second time.
	 */
	const ownBody = (next: string): Partial<NotificationDraft> =>
		next === body ? {} : { bodyTemplate: next, clientNote: "" };

	return (
		<Stack spacing={2}>
			<Grid container spacing={2}>
				<Grid size={{ xs: 12, md: 6 }}>
					<TemplateField
						label="To"
						value={value.toTemplate}
						onChange={(next) => patch({ toTemplate: next })}
						onCommit={(next) => commit({ toTemplate: next })}
						groups={groups}
						labelFor={labelFor}
						list
						readOnly={readOnly}
						placeholder="Add the rep who owns the contact"
						helperText="One address, filled in for each lead."
					/>
				</Grid>

				<Grid size={{ xs: 12, md: 6 }}>
					<TemplateField
						label="BCC"
						value={value.bccTemplate}
						onChange={(next) => patch({ bccTemplate: next })}
						onCommit={(next) => commit({ bccTemplate: next })}
						groups={groups}
						labelFor={labelFor}
						list
						readOnly={readOnly}
						placeholder="Anyone else who should be kept in the loop"
						helperText="Copied in, hidden from each other."
					/>
				</Grid>

				<Grid size={12}>
					<TemplateField
						label="Subject"
						value={value.subjectTemplate}
						onChange={(next) => patch({ subjectTemplate: next })}
						onCommit={(next) => commit({ subjectTemplate: next })}
						groups={groups}
						labelFor={labelFor}
						readOnly={readOnly}
						placeholder="What the subject line should say"
					/>
				</Grid>

				<Grid size={12}>
					{/* The email itself, not a note appended to it. Unlabelled: it opens "Hi," under
					    a To, a BCC and a Subject, and nothing else it could be is on this screen. */}
					<TemplateField
						label="Body"
						hideLabel
						lines
						value={body}
						// Unchanged text is not an edit. Every field here commits on blur, and a body
						// that took the client's own copy of the standard email from a click through
						// it would pin them to today's wording for having looked at it.
						onChange={(next) => patch(ownBody(next))}
						onCommit={(next) => commit(ownBody(next))}
						groups={groups}
						labelFor={labelFor}
						readOnly={readOnly}
						// Three states, because two of them are not "our standard email": one is
						// theirs outright, and one is ours with a note they wrote already in it —
						// which is on the screen, so a line calling it ours reads as a lie.
						helperText={
							bodyIsTheirs
								? "Your wording. Each highlighted value is filled in for every contact."
								: value.clientNote.trim() === ""
									? "Our standard email — change any of it. Each highlighted value is filled in for every contact."
									: "Our standard email with your note in it — change any of it. Each highlighted value is filled in for every contact."
						}
					/>

					{bodyIsTheirs && !readOnly ? (
						<Button
							size="small"
							color="inherit"
							startIcon={<ArrowCounterClockwiseIcon />}
							sx={{ mt: 1 }}
							onClick={() => commit({ bodyTemplate: "", clientNote: "" })}
						>
							Back to the standard email
						</Button>
					) : null}
				</Grid>
			</Grid>

			{/* The check that finds contacts with nobody to email is the one thing on this step that
			    silently costs conversions, so a check that did not run cannot look like a check that
			    came back clean. No count, no reassurance — only what we do not know, and the ask again. */}
			{analysisFailed ? (
				<Alert
					severity="warning"
					action={
						<Button color="inherit" size="small" onClick={() => void recheckList()}>
							Try again
						</Button>
					}
				>
					{
						"We couldn't read your uploaded list, so this screen can't tell you whether any contacts have no rep to email."
					}
				</Alert>
			) : null}

			{/* The count is real, off the uploaded list, and both fixes are one tap. §17 records
			    this as a decided edge case: "no notification email is sent and nothing visibly
			    fails — it is only a log line". */}
			{repColumn && repColumn.missing > 0 ? (
				<Alert
					severity="warning"
					action={
						readOnly ? null : (
							<Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
								<Button color="inherit" size="small" onClick={() => fallbackRef.current?.focus()}>
									Set a fallback address
								</Button>
								<Button
									color="inherit"
									size="small"
									variant={value.excludeContactsWithoutRecipient ? "outlined" : "text"}
									onClick={() => patch({ excludeContactsWithoutRecipient: !value.excludeContactsWithoutRecipient })}
								>
									{value.excludeContactsWithoutRecipient
										? `Excluding those ${formatQuantity(repColumn.missing)} — undo`
										: `Exclude those ${formatQuantity(repColumn.missing)} contacts`}
								</Button>
							</Stack>
						)
					}
				>
					<AlertTitle>
						{`${formatQuantity(repColumn.missing)} of your ${formatQuantity(repColumn.checked)} contacts have no ${
							repColumn.column ?? (repVariable ? labelFor(repVariable).toLowerCase() : "recipient")
						}`}
					</AlertTitle>
					{/* Three states, because the two actions do different things and both of them
					    now bite. Excluding drops the rows at load; a fallback keeps them in the
					    campaign and sends their conversions to one address instead. */}
					<Typography variant="body2">
						{value.excludeContactsWithoutRecipient
							? "We will leave those contacts out of the list when we load it, so nobody is called without someone to tell."
							: value.fallbackEmail.trim()
								? `Those contacts still get called, and if they convert the email goes to ${value.fallbackEmail.trim()} instead of to a rep.`
								: "Those contacts won't trigger an email if they convert. Fill the column, set a fallback address, or leave them out."}
					</Typography>
				</Alert>
			) : null}

			{/* The variable resolved nothing at all: not "some rows are blank" but "no such
			    column", which sends nothing for anybody rather than for twelve people. */}
			{repColumn && repColumn.column === null && repColumn.checked > 0 ? (
				<Alert severity="warning">
					{`Nothing on your list answers “${repVariable ? labelFor(repVariable) : "the recipient"}”, so none of these ${formatQuantity(repColumn.checked)} contacts has an address behind it. Pick something that matches a column you uploaded, or set a fallback address below.`}
				</Alert>
			) : null}

			<TextField
				fullWidth
				disabled={readOnly}
				type="email"
				required={fallbackNeeded}
				error={fallbackMissing}
				label="Fallback address"
				// No default, and no example that looks like one: the old placeholder named a
				// reserved test domain, which accepts nothing and delivers nowhere.
				placeholder="Add an email address"
				value={value.fallbackEmail}
				inputRef={fallbackRef}
				onChange={(event) => patch({ fallbackEmail: event.target.value })}
				// Settled on blur, so the preview can show it catching a row with no rep.
				onBlur={(event) => commit({ fallbackEmail: event.target.value })}
				helperText={
					fallbackMissing
						? "Some contacts have no rep, so their conversions would go to nobody."
						: "Used when a contact has no rep on the list."
				}
			/>

			{/*
			 * "See what you can drop into these fields" opened a full catalogue of every variable,
			 * each with its own description — a click-to-expand reference panel duplicating the
			 * "+ variable" picker that sits on every field above, which offers the same list with
			 * the same descriptions and puts the choice straight into the template. Deleted.
			 */}

			{/* Resolved against a real row wherever there is one to resolve against. §08 note 8:
			    resolved values are the difference between "I think that's right" and "yes, send
			    it" — a card full of braces only proves the template parses. */}
			<Card variant="outlined" sx={{ bgcolor: "background.level1", p: 2 }}>
				<Stack spacing={1}>
					<Stack direction="row" spacing={1} sx={{ alignItems: "center", justifyContent: "space-between" }}>
						<Typography variant="subtitle2">
							{preview?.fromRealRow ? "Preview — using a real row from your list" : "The email we send"}
						</Typography>
						{previewMutation.isPending ? (
							<CircularProgress size={14} />
						) : previewMutation.isError ? (
							// Only where asking again could answer differently. A list that came back
							// with no rows will come back with no rows.
							<Button size="small" color="inherit" onClick={() => retryPreview()}>
								Try again
							</Button>
						) : null}
					</Stack>

					<Typography variant="caption" color={note.failed ? "warning.main" : "text.secondary"}>
						{note.text}
					</Typography>

					<Divider />

					{preview?.fromRealRow ? (
						<React.Fragment>
							{/* Three outcomes for one line, and the middle one is the whole point of
							    the fallback field: this row has no rep, and somebody still hears. */}
							<Typography variant="body2" color={preview.to ? "text.primary" : "warning.main"}>
								To {preview.to ?? "— nobody to send to, so no email would go out"}
								{preview.usedFallback ? " — the fallback, because this contact has no rep of their own" : ""}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								BCC {preview.bcc.length > 0 ? preview.bcc.join(", ") : "— none"}
							</Typography>
							<Typography variant="body2">Subject {preview.subject || "— not set"}</Typography>

							<Divider />

							<Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
								{preview.body}
							</Typography>

							{preview.unresolved.length > 0 ? (
								<Typography variant="caption" color="warning.main">
									{`This row has nothing behind ${preview.unresolved.map((name) => labelFor(name)).join(", ")}. Those parts arrive empty.`}
								</Typography>
							) : null}
						</React.Fragment>
					) : (
						<React.Fragment>
							<Typography variant="body2" component="div" color="text.secondary">
								To <TemplateText template={value.toTemplate} labelFor={labelFor} empty="— not set" />
							</Typography>
							<Typography variant="body2" component="div" color="text.secondary">
								BCC <TemplateText template={value.bccTemplate} labelFor={labelFor} empty="— none" />
							</Typography>
							<Typography variant="body2" component="div" color="text.secondary">
								Subject{" "}
								{/* An unset subject still shows what we would send, and it is a
								    template like any other — so it is read out as pills too. */}
								<TemplateText
									template={
										value.subjectTemplate.trim() === ""
											? `${campaignTitle} — {{lead__disposition}}`
											: value.subjectTemplate
									}
									labelFor={labelFor}
									empty="— not set"
								/>
							</Typography>

							<Divider />

							{/* The body being edited above, read back with the pills in place. It was a
							    third copy of the standard email written out here by hand, which is
							    one copy too many now that the client can change it. */}
							<Typography variant="body2" component="div" sx={{ whiteSpace: "pre-line" }}>
								<TemplateText template={body} labelFor={labelFor} empty="" />
							</Typography>
						</React.Fragment>
					)}
				</Stack>
			</Card>

			{/* "Nothing is emailed until an outcome above has Email switched on" is already the
			    heading of the section this editor sits inside. Once was enough. */}
		</Stack>
	);
}
